/**
 * Pre-Exchange Checklist Configuration
 * 
 * Defines all sections and items for the pre-exchange workflow.
 * Each item has conditional visibility based on transaction_type, is_leasehold, is_mortgaged.
 * 
 * Item types: "checkbox", "checkbox_date", "checkbox_amount", "checkbox_notes",
 *             "radio", "radio_date", "radio_notes", "date", "multiline", "signature"
 */

const PRE_EXCHANGE_CONFIG = {
  id: "pre_exchange",
  title: "Pre-Exchange Approval Checklist",
  subtitle: "Residential Conveyancing — Workflow 16",
  pdfFooter: "This checklist is aligned with the Law Society Conveyancing Protocol (2019) and the Law Society Conveyancing Quality Scheme Core Practice Management Standards (Risk Management, File & Case Management, Client Care).",
  sections: [
    {
      id: "title_property",
      title: "Title & Property",
      items: [
        { id: "1.1", label: "Official copies of title obtained and reviewed", type: "checkbox", applies: ["sale", "purchase", "linked"] },
        { id: "1.2", label: "Title plan reviewed and boundaries confirmed", type: "checkbox", applies: ["sale", "purchase", "linked"] },
        { id: "1.3", label: "Any charges/restrictions on title identified and addressed", type: "checkbox", applies: ["sale", "purchase", "linked"] },
        { id: "1.4", label: "Seller's title deduced (unregistered land)", type: "checkbox", applies: ["purchase", "linked"] },
        { id: "1.5", label: "Title insurance obtained (if applicable)", type: "checkbox_notes", applies: ["purchase", "linked"] },
      ],
    },
    {
      id: "searches",
      title: "Searches",
      items: [
        { id: "2.1", label: "Local authority search obtained", type: "checkbox_date", applies: ["purchase", "linked"], deadline_type: "search_expiry" },
        { id: "2.2", label: "Environmental search obtained", type: "checkbox_date", applies: ["purchase", "linked"] },
        { id: "2.3", label: "Water & drainage search obtained", type: "checkbox_date", applies: ["purchase", "linked"] },
        { id: "2.4", label: "Chancel repair search obtained", type: "checkbox_date", applies: ["purchase", "linked"] },
        { id: "2.5", label: "Mining/tin/brine search (if applicable)", type: "checkbox", applies: ["purchase", "linked"] },
        { id: "2.6", label: "All search results reviewed and reported to client", type: "checkbox", applies: ["purchase", "linked"] },
      ],
    },
    {
      id: "contract",
      title: "Contract",
      // BM-H4384: opens the conveyancing agent on this matter with the task picked.
      agentSkills: ["Enquiries review"],
      items: [
        { id: "3.1", label: "Draft contract prepared/approved", type: "checkbox", applies: ["sale", "purchase", "linked"] },
        { id: "3.2", label: "Special conditions reviewed and agreed", type: "checkbox", applies: ["sale", "purchase", "linked"] },
        { id: "3.3", label: "Fixtures, fittings & contents form (TA10) completed", type: "checkbox", applies: ["sale", "linked"] },
        { id: "3.4", label: "Property information form (TA6) completed", type: "checkbox", applies: ["sale", "linked"] },
        { id: "3.5", label: "Leasehold information form (TA7) completed", type: "checkbox", applies: ["sale", "linked"], condition: "is_leasehold" },
        { id: "3.6", label: "All pre-contract enquiries raised and answered", type: "checkbox", applies: ["purchase", "linked"] },
        { id: "3.7", label: "Any outstanding enquiries resolved or undertakings agreed", type: "checkbox_notes", applies: ["purchase", "linked"] },
        { id: "3.8", label: "Contract engrossed (two copies)", type: "checkbox", applies: ["sale", "purchase", "linked"] },
        { id: "3.9", label: "Client signed contract", type: "checkbox", applies: ["sale", "purchase", "linked"] },
      ],
    },
    {
      id: "mortgage_finance",
      title: "Mortgage & Finance",
      // BM-H4384: opens the conveyancing agent on this matter with the task picked.
      agentSkills: ["Mortgage offer summary", "Source of funds review"],
      items: [
        { id: "4.1", label: "Mortgage offer received and reviewed", type: "checkbox", applies: ["purchase", "linked"], condition: "is_mortgaged", deadline_type: "mortgage_offer" },
        { id: "4.2", label: "Mortgage conditions satisfied", type: "checkbox", applies: ["purchase", "linked"], condition: "is_mortgaged" },
        { id: "4.3", label: "Certificate of title/solicitor's undertaking ready", type: "checkbox", applies: ["purchase", "linked"], condition: "is_mortgaged" },
        { id: "4.4", label: "Deposit arrangements confirmed", type: "checkbox_amount", applies: ["purchase", "linked"] },
        { id: "4.5", label: "Source of funds verified (AML)", type: "checkbox", applies: ["purchase", "linked"] },
        { id: "4.6", label: "Existing mortgage redemption figure obtained", type: "checkbox", applies: ["sale", "linked"], condition: "is_mortgaged" },
      ],
    },
    {
      id: "completion_arrangements",
      title: "Completion Arrangements",
      items: [
        { id: "transaction_value", label: "Transaction value", type: "checkbox_amount", applies: ["sale", "purchase", "linked"], tooltip: "Enter the agreed purchase/sale price. This flows automatically to the Completions Book." },
        { id: "5.1", label: "Completion date agreed", type: "checkbox_date", applies: ["sale", "purchase", "linked"], deadline_type: "completion_date", tooltip: "This date becomes the single source of truth for the matter. It flows automatically to the Completions Book and Pre-Completion Checklist. Changing it after exchange triggers an audit log and email notification." },
        { id: "5.2", label: "Completion statement prepared and approved by client", type: "checkbox", applies: ["sale", "purchase", "linked"] },
        { id: "5.3", label: "Undertakings agreed (discharge of seller's mortgage)", type: "checkbox", applies: ["purchase", "linked"] },
        { id: "5.4", label: "Keys arrangements confirmed", type: "checkbox", applies: ["sale", "purchase", "linked"] },
        { id: "5.5", label: "Chain confirmed (if linked transactions)", type: "checkbox", applies: ["linked"] },
      ],
    },
    {
      id: "leasehold_specific",
      title: "Leasehold Specific",
      condition: "is_leasehold",
      items: [
        { id: "6.1", label: "Leasehold information form (TA7) reviewed", type: "checkbox", applies: ["purchase", "linked"] },
        { id: "6.2", label: "Management pack obtained and reviewed", type: "checkbox_date", applies: ["purchase", "linked"] },
        { id: "6.3", label: "Ground rent — amount confirmed and reviewed", type: "checkbox_amount", applies: ["purchase", "linked"] },
        { id: "6.4", label: "Service charge accounts reviewed (last 3 years)", type: "checkbox", applies: ["purchase", "linked"] },
        { id: "6.5", label: "Major works/section 20 notices reviewed", type: "checkbox_notes", applies: ["purchase", "linked"] },
        { id: "6.6", label: "Licence to assign required and obtained", type: "checkbox", applies: ["sale", "purchase", "linked"] },
        { id: "6.7", label: "Deed of covenant required and prepared", type: "checkbox", applies: ["purchase", "linked"] },
        { id: "6.8", label: "Unexpired lease term — minimum lender requirements met", type: "checkbox", applies: ["purchase", "linked"], condition: "is_mortgaged" },
        { id: "6.9", label: "Landlord's consent obtained (if required)", type: "checkbox", applies: ["sale", "purchase", "linked"] },
      ],
    },
    {
      id: "client_communication",
      title: "Client Communication",
      // BM-H4384: opens the conveyancing agent on this matter with the task picked.
      agentSkills: ["Report on title"],
      items: [
        { id: "6.1", label: "Exchange report sent to client", type: "checkbox", applies: ["sale", "purchase", "linked"] },
        { id: "6.2", label: "Client confirmed ready to exchange", type: "checkbox", applies: ["sale", "purchase", "linked"] },
        { id: "6.3", label: "Client advised on insurance (purchase)", type: "checkbox", applies: ["purchase", "linked"] },
      ],
    },
    {
      id: "client_understanding_risk",
      title: "Client Understanding & Risk",
      // BM-H4384: opens the conveyancing agent on this matter with the task picked.
      agentSkills: ["AML/ID summary"],
      items: [
        { id: "7.1", label: "FORF/Risk assessment completed", type: "checkbox", applies: ["sale", "purchase", "linked"], autoCheck: "forf" },
        { id: "7.2", label: "Conflict check clear", type: "checkbox", applies: ["sale", "purchase", "linked"], autoCheck: "conflict_check" },
        { id: "7.3", label: "AML checks complete", type: "checkbox", applies: ["sale", "purchase", "linked"], autoCheck: "thirdfort" },
        { id: "7.4", label: "File review by supervisor (if required)", type: "checkbox", applies: ["sale", "purchase", "linked"] },
        // CQS Enhancement fields
        { id: "cqs_file_note_updated", label: "Have you updated a file note today to record any key advice or risk discussions?", type: "radio", applies: ["sale", "purchase", "linked"], required: true },
        { id: "cqs_client_costs_updated", label: "Have you confirmed any changes in costs or disbursements (e.g. extra searches/indemnities) to the client?", type: "radio", applies: ["sale", "purchase", "linked"], required: true },
        { id: "cqs_client_costs_date", label: "Date of last costs update to client", type: "date", applies: ["sale", "purchase", "linked"], conditionalOn: "cqs_client_costs_updated", conditionalValue: "yes" },
      ],
    },
    {
      id: "cqs_protocol_compliance",
      title: "CQS & Protocol Compliance",
      items: [
        { id: "cqs_risk_issue_flagged", label: "Are there any outstanding risk issues that should be flagged for CQS file review or PI risk log?", type: "radio", applies: ["sale", "purchase", "linked"], required: true },
        { id: "cqs_risk_issue_details", label: "If Yes, briefly describe the risk and planned mitigation", type: "multiline", applies: ["sale", "purchase", "linked"], conditionalOn: "cqs_risk_issue_flagged", conditionalValue: "yes" },
        { id: "cqs_risk_followup_date", label: "Follow-up/review date for this risk (if applicable)", type: "date", applies: ["sale", "purchase", "linked"], conditionalOn: "cqs_risk_issue_flagged", conditionalValue: "yes" },
        { id: "cqs_supervisory_review", label: "If a formal supervisor review is not required under policy, do you recommend a discretionary supervisor review at this stage?", type: "radio", applies: ["sale", "purchase", "linked"], required: true },
        { id: "cqs_supervisory_review_reason", label: "If Yes, state why (e.g. high-value, unusual structure, risk flags)", type: "multiline", applies: ["sale", "purchase", "linked"], conditionalOn: "cqs_supervisory_review", conditionalValue: "yes" },
      ],
    },
    {
      id: "fee_earner_signoff",
      title: "Fee Earner Sign-off",
      items: [
        { id: "8.1", label: "Fee earner confirms all items complete and ready to exchange", type: "signature", applies: ["sale", "purchase", "linked"] },
      ],
    },
  ],
};

export default PRE_EXCHANGE_CONFIG;
